import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Store } from '@ngrx/store';

import { combineLatest, Subscription } from 'rxjs';

import { AppState } from '../../../../../state';
import { selectSelectedIntervention } from '../../../../state/intervention/intervention.selectors';
import { selectSelectedLanguage } from 'src/app/dashboard/state/study/study.selectors';
import { selectInformativePages } from '../../../../state/informative-page/informative-page.selectors';
import { selectModules } from '../../../../state/module/module.selectors';
import {
  selectContentItemsSortingOrder,
  selectInterventionContentItems,
} from '../../../../state/intervention-content-item/intervention-content-item.selectors';
import {
  addContentItemToIntervention,
  removeContentItemFromIntervention,
  saveContentItemsSortingOrder,
} from '../../../../state/intervention-content-item/intervention-content-item.actions';
import { Module } from '../../../../../models/module';
import { InformativePage } from '../../../../../models/informative-page';
import { Language } from '../../../../../models/language';
import { InterventionContentItem } from '../../../../../models/intervention-content-item';
import { Intervention } from '../../../../../models/intervention';
import { DASHBOARD_ROUTES } from '../../../../dashboard.routes';
import { getModule } from '../../../../state/module/module.actions';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';
import { setStartModule } from '../../../../state/intervention/intervention.actions';
import { getInterventionContentItemId, SELECT_LIST_ITEM_TYPE } from '../../../helpers/intervention-helper';

export const INTERVENTION_CONTENT_TOOLTIP =
  'It is important to put an Informative Page as a last item in the list.\n' +
  'However, Email Page cannot be placed as a last or second to last item in the list.\n' +
  '\n' +
  'Please, after adding Informative Page check if the conditions above are met and change place of the item if needed. ';

@Component({
  selector: 'dashboard-intervention-content',
  templateUrl: './intervention-content.component.html',
  styleUrls: ['./intervention-content.component.scss'],
})
export class InterventionContentComponent implements OnInit, OnDestroy {
  @Input() isEdit: boolean;

  interventionContentForm: FormGroup;

  content = true;
  module = true;

  selectedLanguage: Language;
  selectedIntervention: Intervention;

  ItemType = SELECT_LIST_ITEM_TYPE;

  modules: Module[];
  informativePages: InformativePage[];
  interventionContentItems: InterventionContentItem[];

  interventionModulesAndInformativePages: (
    | (Module & { isCompassStart: boolean })
    | (InformativePage & { isCompassStart: boolean })
  )[];
  interventionModulesSelectList: Module[];
  interventionInformativePageSelectList: InformativePage[];

  thereIsStartModule = false;

  SELECT_LIST_ITEM_TYPE = SELECT_LIST_ITEM_TYPE;
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  INTERVENTION_CONTENT_TOOLTIP = INTERVENTION_CONTENT_TOOLTIP;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private router: Router,
    public privilegeService: PrivilegeService
  ) {}

  get moduleId(): AbstractControl {
    return this.interventionContentForm.get('moduleId') as AbstractControl;
  }

  get informativePageId(): AbstractControl {
    return this.interventionContentForm.get('informativePageId') as AbstractControl;
  }

  ngOnInit(): void {
    this.interventionContentForm = this.formBuilder.group({
      moduleId: [null],
      informativePageId: [null],
    });

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => (this.selectedLanguage = language))
    );
    this.subscriptions.add(
      this.store
        .select(selectSelectedIntervention)
        .subscribe((intervention) => (this.selectedIntervention = intervention))
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(selectModules),
        this.store.select(selectInterventionContentItems),
        this.store.select(selectInformativePages),
        this.store.select(selectContentItemsSortingOrder),
      ]).subscribe(([modules, interventionContentItems, informativePages, contentItemsSortingOrder]) => {
        this.modules = modules.filter((module) => !module.enrolment);
        this.interventionContentItems = interventionContentItems;
        this.informativePages = informativePages;

        this.interventionModulesAndInformativePages = this.interventionContentItems.map((item) => {
          if (item.module) {
            if (item.isCompassStart) {
              this.thereIsStartModule = true;
            }

            return { ...item.module, type: SELECT_LIST_ITEM_TYPE.MODULE, isCompassStart: item.isCompassStart };
          }
          if (item.page) {
            return { ...item.page, type: SELECT_LIST_ITEM_TYPE.INFORMATIVE_PAGE, isCompassStart: false };
          }
        });

        if (!this.thereIsStartModule) {
          const theFirstModuleIndex = this.interventionModulesAndInformativePages.findIndex(
            (item) => item.type === SELECT_LIST_ITEM_TYPE.MODULE
          );

          if (theFirstModuleIndex > -1) {
            this.interventionModulesAndInformativePages[theFirstModuleIndex] = {
              ...this.interventionModulesAndInformativePages[theFirstModuleIndex],
              isCompassStart: true,
            };
            this.setStartModule(this.interventionModulesAndInformativePages[theFirstModuleIndex]);
          }
        }

        this.createSelectList();

        const orderOfItemIds = [];

        contentItemsSortingOrder.forEach((sortingOrderItem) => {
          this.interventionContentItems.forEach((contentItem) => {
            if (
              contentItem.hasOwnProperty(sortingOrderItem?.type) &&
              sortingOrderItem.id === contentItem.id &&
              !orderOfItemIds.includes(contentItem[sortingOrderItem?.type].id)
            ) {
              orderOfItemIds.push(contentItem[sortingOrderItem?.type].id);
            }
          });
        });

        this.interventionModulesAndInformativePages.sort(
          (item1, item2) => orderOfItemIds.indexOf(item1?.id) - orderOfItemIds.indexOf(item2?.id)
        );
      })
    );
  }

  createSelectList(): void {
    this.interventionModulesSelectList = this.modules.filter(
      (module) => !this.interventionContentItems.find((moduleToBeRemoved) => moduleToBeRemoved.module?.id === module.id)
    );
    this.interventionInformativePageSelectList = this.informativePages.filter(
      (informativePage) =>
        !this.interventionContentItems.find(
          (informativePageToBeRemoved) => informativePageToBeRemoved.page?.id === informativePage.id
        )
    );
  }

  onAddContentItem(itemType: string): void {
    if (
      (itemType === SELECT_LIST_ITEM_TYPE.MODULE && !this.moduleId.value) ||
      (itemType === SELECT_LIST_ITEM_TYPE.INFORMATIVE_PAGE && !this.informativePageId.value)
    ) {
      return;
    }

    this.store.dispatch(
      addContentItemToIntervention({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        itemId: itemType === SELECT_LIST_ITEM_TYPE.MODULE ? this.moduleId.value : this.informativePageId.value,
        itemType,
      })
    );
  }

  onDelete(item: (Module & { isCompassStart: boolean }) | (InformativePage & { isCompassStart: boolean })): void {
    const interventionContentItemId = getInterventionContentItemId(item, this.interventionContentItems);

    if (item.type === SELECT_LIST_ITEM_TYPE.MODULE && item.isCompassStart) {
      this.thereIsStartModule = false;
    }

    this.store.dispatch(
      removeContentItemFromIntervention({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        itemId: item.id,
        itemType: item.type,
        interventionContentItemId,
      })
    );
  }

  dropItem(event: CdkDragDrop<(Module | InformativePage)[]>) {
    moveItemInArray(this.interventionModulesAndInformativePages, event.previousIndex, event.currentIndex);
    this.saveItemSortingOrder(event.container.data);
  }

  saveItemSortingOrder(itemsArray: (Module | InformativePage)[]): void {
    const sortingOrder = this.getContentItemSortOrderArray(itemsArray);
    this.store.dispatch(
      saveContentItemsSortingOrder({
        studyId: this.selectedIntervention.studyId,
        interventionId: this.selectedIntervention.id,
        sortingOrder,
      })
    );
  }

  onOpenModulesTab(contentItem: Module): void {
    if (
      contentItem?.type === SELECT_LIST_ITEM_TYPE.MODULE &&
      (this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL) ||
        this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.VIEW))
    ) {
      this.store.dispatch(getModule({ studyId: contentItem?.studyId, moduleId: contentItem?.id }));
      this.router.navigate([`${DASHBOARD_ROUTES.STUDIES}/${this.selectedIntervention.studyId}/modules`]);
    }
  }

  getContentItemSortOrderArray(items): InterventionContentItem[] {
    return items?.map((item) => {
      const interventionContentItemId = getInterventionContentItemId(item, this.interventionContentItems);
      return { type: item.type, id: interventionContentItemId };
    });
  }

  setStartModule(item: (Module & { isCompassStart: boolean }) | (InformativePage & { isCompassStart: boolean })): void {
    if (!item.isCompassStart) {
      this.store.dispatch(
        setStartModule({
          studyId: this.selectedIntervention.studyId,
          interventionId: this.selectedIntervention.id,
          moduleId: item.id,
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
